import { BACKEND_URL } from "./api/config";
import React, { lazy, Suspense, useState, useEffect } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import axios from "axios";

import Navbar from "./components/Navbar";
import Login from "./pages/Login";
const Dashboard = lazy(() => import("./pages/Dashboard"));
const ChecklistPage = lazy(() => import("./pages/ChecklistPage"));
const SixSAuditChecklistPage = lazy(() => import("./pages/SixSAuditChecklistPage"));
const NACReconciliationPage = lazy(() => import("./pages/NACReconciliationPage"));
const AdminPage = lazy(() => import("./pages/Admin/AdminPage"));
const DailyKaizen = lazy(() => import("./pages/DailyKaizen"));
const LcmSetup = lazy(() => import("../src/LCM/lcmsetup"));
const LcmMarks = lazy(() => import("../src/LCM/lcmmarks"));
const LcmDashboard = lazy(() => import("../src/LCM/lcmdashboard"));
const AuditsSetup = lazy(() => import("./pages/AuditsSetup"));
const VisitorFeedbackDashboard = lazy(() => import("./tukuzaklab/VisitorFeedbackDashboard"));
const TukuzaLabDashboard = lazy(() => import("../src/tukuzaklab/tukuzalabdashboard"));
const TukuzaLabSetup = lazy(() => import("../src/tukuzaklab/tukuzalabsetup"));
const TukuzaLabSubmissions = lazy(() => import("../src/tukuzaklab/VisitorFeedback"));
const ClauseResponseDashboard = lazy(() => import("../src/Compliance/ClauseResponseDashboard"));
const ClauseResponses = lazy(() => import("./Compliance/ClauseResponses"));
const ClauseSetup = lazy(() => import("./Compliance/ClauseSetup"));
const WelcomePage = lazy(() => import("./pages/WelcomePage"));
const SkillsMatrixPage = lazy(() => import("./SkillsMatrix"));
const ProcessAuditAnalytics = lazy(() => import("./pages/ProcessAuditAnalytics"));
const SixSAuditAnalytics = lazy(() => import("./pages/SixSAuditAnalytics"));
const TrainingPlans = lazy(() => import("./DailyKaizenHooks/TrainingPlans"));
const AuditDrafts = lazy(() => import("./pages/AuditDrafts"));
const AuditSubmissions = lazy(() => import("./pages/AuditSubmissions"));
const PerformanceSettings = lazy(() => import("./GembaPerformance/PerformanceSettings"));
const API = BACKEND_URL;

const readCachedPolicy = () => {
  try { return JSON.parse(localStorage.getItem("access_policy")) || {}; }
  catch { return {}; }
};

// Scheduler pages
const SchedulerTeamDashboard = lazy(() => import("./Scheduler/SchedulerTeamDashboard"));
const SchedulerTeamTasks = lazy(() => import("./Scheduler/SchedulerTeamTasks"));
const SchedulerTeamMembersSetup = lazy(() => import("./Scheduler/SchedulerTeam&MembersSetup"));

// Kamishibai pages
const KamishibaiBoard = lazy(() => import("./Kamishibai/KamishibaiBoard"));
const KamishibaiSetup = lazy(() => import("./Kamishibai/KamishibaiSetup"));
const KamishibaiResponses = lazy(() => import("./Kamishibai/KamishibaiResponses"));

// helper: decode JWT payload safely
const App = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(() => Boolean(localStorage.getItem("token")));
  const [permissions, setPermissions] = useState(readCachedPolicy);
  const [accessReady, setAccessReady] = useState(() => !localStorage.getItem("token"));
  const location = useLocation();

  /* Keep route guards and navigation on the same live access policy. */
  useEffect(() => {
    const token = localStorage.getItem("token");
    setIsAuthenticated(Boolean(token));
    if (!token) { setPermissions({}); setAccessReady(true); return undefined; }
    let active = true;
    let generation = 0;
    const refresh = async () => {
      const requestId = ++generation;
      try {
        const { data } = await axios.get(`${API}/api/auth/users/me/`, { headers: { Authorization: `Bearer ${token}` } });
        if (!active || requestId !== generation) return;
        const next = data.permissions || {};
        localStorage.setItem("access_policy", JSON.stringify(next));
        localStorage.setItem("current_user", JSON.stringify(data));
        setPermissions(next);
      } catch (_) {
        if (active && !localStorage.getItem("access_policy")) setPermissions({});
      } finally { if (active) setAccessReady(true); }
    };
    const visibleRefresh = () => { if (document.visibilityState === "visible") refresh(); };
    refresh();
    window.addEventListener("tms:access-changed", refresh);
    window.addEventListener("focus", visibleRefresh);
    document.addEventListener("visibilitychange", visibleRefresh);
    const timer = window.setInterval(visibleRefresh, 60000);
    return () => { active = false; window.clearInterval(timer); window.removeEventListener("tms:access-changed", refresh); window.removeEventListener("focus", visibleRefresh); document.removeEventListener("visibilitychange", visibleRefresh); };
  }, [isAuthenticated]);

  const handleLogin = () => {
    setPermissions(readCachedPolicy());
    setAccessReady(true);
    setIsAuthenticated(true);
  };
  const handleLogout = () => {
    localStorage.clear();
    setIsAuthenticated(false);
  };

  // derived permission flags
  const can = (code) => permissions["*"] || Boolean(permissions[code]);
  const canAccessClauseSetup = can("compliance.setup");

  // Check if current route is welcome page (no padding needed)
  const isWelcomePage = location.pathname === "/" || location.pathname === "/welcome";
  const isDashboardPage = location.pathname === "/dashboard";
  const isAuditFormPage = /^\/(audit-forms|sixs-audit)\//.test(location.pathname);
  const isAuditAnalyticsPage = ["/analytics/process-audits", "/analytics/6s-audits"].includes(location.pathname);

  /* ================= LOGIN LAYOUT ================= */
  if (!isAuthenticated) {
    return (
      <Suspense fallback={<div className="app-route-loading" role="status">Loading…</div>}>
      <Routes>
        <Route path="/login" element={<Login onLogin={handleLogin} />} />
        <Route path="/tukuzalab/tukuzalabsubmissions" element={<TukuzaLabSubmissions />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
      </Suspense>
    );
  }
  if (!accessReady) return <div className="app-route-loading" role="status">Loading your access policy…</div>;

  /* ================= AUTHENTICATED LAYOUT ================= */
  return (
    <div className="app-container">
      <Navbar onLogout={handleLogout} permissions={permissions} />

      <div className={`main-content with-navbar ${["/tukuzalab/tukuzalabsubmissions", "/tukuzalab/visitor-feedback"].includes(location.pathname) ? "lab-feedback-active" : ""} ${location.pathname === "/trainings" ? "trainings-active" : ""} ${location.pathname === "/kamishibai/board" ? "kamishibai-board-active" : ""} ${isAuditFormPage ? "audit-form-active" : ""} ${isWelcomePage ? "welcome-active" : ""} ${isDashboardPage ? "dashboard-active" : ""} ${isAuditAnalyticsPage ? "audit-analytics-active" : ""} ${location.pathname === "/audit-submissions" ? "submissions-active" : ""}`}>
        <Suspense fallback={<div className="app-route-loading" role="status">Loading module…</div>}>
        <Routes>
          {/* Welcome Page — first stop after login */}
          <Route path="/" element={<WelcomePage permissions={permissions} />} />
          <Route path="/welcome" element={<WelcomePage permissions={permissions} />} />

          <Route path="/admin/gemba-performance" element={can("gemba_performance.weights") ? <PerformanceSettings /> : <Navigate to="/dashboard" replace />} />
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/analytics/process-audits" element={<ProcessAuditAnalytics />} />
          <Route path="/analytics/6s-audits" element={<SixSAuditAnalytics />} />
          <Route path="/audit-forms/:gembaId" element={<ChecklistPage canSubmit={Boolean(can("process_audit.submit"))} />} />
          <Route path="/sixs-audit/:gembaId" element={<SixSAuditChecklistPage />} />
          <Route path="/nac-reconciliation" element={<NACReconciliationPage />} />
          <Route path="/kamishibai/mappings" element={can("kamishibai.mapping") ? <Navigate to="/kamishibai/setup" replace /> : <Navigate to="/welcome" replace />} />
          <Route path="/audit-drafts" element={can("audit_drafts.view") ? <AuditDrafts permissions={permissions} /> : <Navigate to="/welcome" replace />} />
          <Route path="/audit-submissions" element={["process", "sixs", "kamishibai"].some(k => ["view", "create", "update", "delete"].some(a => can(`${k}_submissions.${a}`))) ? <AuditSubmissions permissions={permissions} /> : <Navigate to="/welcome" replace />} />
          <Route path="/trainings" element={<TrainingPlans permissions={permissions} />} />
          <Route path="/daily-kaizen" element={<DailyKaizen />} />
          <Route path="/skills-matrix" element={<SkillsMatrixPage />} />
          <Route path="/lcm/lcmsetup" element={<LcmSetup />} />
          <Route path="/lcm/lcmmarks" element={<LcmMarks />} />
          <Route path="/lcm/lcmdashboard" element={<LcmDashboard />} />

          <Route path="/tukuzalab/tukuzalabdashboard" element={<TukuzaLabDashboard />} />
          <Route path="/tukuzalab/visitor-feedback" element={(permissions["*"] || permissions["visitor_feedback.view"] === "all") ? <VisitorFeedbackDashboard /> : <Navigate to="/welcome" replace />} />
          <Route path="/tukuzalab/tukuzalabsubmissions" element={<TukuzaLabSubmissions />} />
          <Route
            path="/tukuzalab/tukuzalabsetup"
            element={
              can("sparklab.setup")
                ? <TukuzaLabSetup />
                : <Navigate to="/tukuzalab/tukuzalabdashboard" replace />
            }
          />

          <Route
            path="/audits-setup"
            element={
              can("audits.setup")
                ? <AuditsSetup />
                : <Navigate to="/dashboard" replace />
            }
          />

          <Route
            path="/admin"
            element={
              can("admin.access_control")
                ? <AdminPage permissions={permissions} />
                : <Navigate to="/dashboard" replace />
            }
          />

          {/* Scheduler Routes*/}
          <Route path="/scheduler/team-dashboard" element={<SchedulerTeamDashboard />} />
          <Route path="/scheduler/team-tasks" element={<SchedulerTeamTasks />} />
          <Route
            path="/scheduler/team-members-setup"
            element={
              can("scheduler.setup")
                ? <SchedulerTeamMembersSetup />
                : <Navigate to="/scheduler/team-dashboard" replace />
            }
          />

          {/* Kamishibai Routes */}
          <Route path="/kamishibai/board" element={<KamishibaiBoard />} />
          <Route path="/kamishibai/responses" element={<KamishibaiResponses />} />
          <Route
            path="/kamishibai/setup"
            element={
              (can("kamishibai.setup") || can("kamishibai.mapping"))
                ? <KamishibaiSetup permissions={permissions} />
                : <Navigate to="/kamishibai/board" replace />
            }
          />

          {/* Compliance Routes */}
          <Route path="/compliance/clause-response-dashboard" element={<ClauseResponseDashboard />} />
          <Route path="/compliance/clause-responses" element={<ClauseResponses />} />
          <Route
            path="/compliance/clause-setup"
            element={
              canAccessClauseSetup
                ? <ClauseSetup />
                : <Navigate to="/compliance/clause-response-dashboard" replace />
            }
          />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </Suspense>
      </div>
    </div>
  );
};

export default App;

